/**
 * ReferencesPage — Reference Library (light theme).
 */

import { useState, useEffect } from 'react'
import {
  Plus, LayoutList, LayoutGrid, Pencil, Trash2,
  Loader2, Sparkles, X, ChevronUp, ChevronDown, BookOpen, AlertCircle,
} from 'lucide-react'
import api from '../../lib/api'

const SURFACE = '#FFFFFF'
const BG      = '#F4F7FC'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'
const GOLD_BG = 'rgba(37,99,235,0.06)'

// ─── Type chip ────────────────────────────────────────────────────────────────

const TYPE_CFG = {
  JOURNAL: { bg: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: 'rgba(37,99,235,0.25)' },
  BOOK:    { bg: 'rgba(22,163,74,0.08)',  color: '#15803D', border: 'rgba(22,163,74,0.25)' },
  REPORT:  { bg: 'rgba(37,99,235,0.08)',  color: '#1D4ED8', border: 'rgba(37,99,235,0.25)' },
  WEBSITE: { bg: 'rgba(80,98,128,0.08)',  color: '#506280', border: 'rgba(80,98,128,0.25)' },
  OTHER:   { bg: 'rgba(80,98,128,0.08)',  color: '#506280', border: 'rgba(80,98,128,0.25)' },
}

function TypeChip({ type }) {
  const c = TYPE_CFG[type] || TYPE_CFG.OTHER
  return (
    <span className="font-mono text-xs px-2 py-0.5 rounded-md"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {type}
    </span>
  )
}

// ─── APA helper ───────────────────────────────────────────────────────────────

function buildApa(r) {
  const y   = r.year   ? `(${r.year})` : '(n.d.)'
  const vol = r.volume ? `, ${r.volume}` : ''
  const iss = r.issue  ? `(${r.issue})` : ''
  const pg  = r.pages  ? `, ${r.pages}` : ''
  const d   = r.doi    ? ` https://doi.org/${r.doi}` : (r.url ? ` ${r.url}` : '')
  if (r.type === 'BOOK') return `${r.authors} ${y}. *${r.title}*. ${r.journal || ''}${d}`.trim()
  return `${r.authors} ${y}. ${r.title}. *${r.journal || ''}*${vol}${iss}${pg}.${d}`.trim()
}

// ─── Shared input styles ─────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2.5 rounded-lg font-sans text-sm placeholder:text-[#8EA4BC] outline-none transition-all"
const inputSty = { background: BG, border: `1px solid ${BORDER}`, color: TEXT }
const focusHandlers = {
  onFocus: e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.45)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.10)' },
  onBlur:  e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = 'none' },
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ onClose, width = 600, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(12,22,41,0.45)' }}
      onClick={onClose}>
      <div className="rounded-2xl max-h-[90vh] overflow-y-auto shadow-elevated"
        style={{ background: SURFACE, border: `1px solid ${BORDER}`, width: '100%', maxWidth: width }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

// ─── Manual form ──────────────────────────────────────────────────────────────

const EMPTY_REF = { authors: '', year: '', title: '', journal: '', volume: '', issue: '', pages: '', doi: '', url: '', type: 'JOURNAL' }

function ManualForm({ initial = EMPTY_REF, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial)
  const set = k => e => setForm(prev => ({ ...prev, [k]: e.target.value }))
  const apaPreview = form.authors && form.title ? buildApa(form) : ''
  const canSave    = form.authors.trim() && form.title.trim()

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>
            Authors <span style={{ color: GOLD }}>*</span>
          </label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Smith, J., & Jones, M." value={form.authors} onChange={set('authors')} />
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>
            Title <span style={{ color: GOLD }}>*</span>
          </label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Paper or book title" value={form.title} onChange={set('title')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Year</label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="2024" type="number" value={form.year} onChange={set('year')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Type</label>
          <select className={inputCls} style={{ ...inputSty, cursor: 'pointer' }} value={form.type} onChange={set('type')}>
            {['JOURNAL','BOOK','REPORT','WEBSITE','OTHER'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Journal / Publisher</label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Journal of …" value={form.journal} onChange={set('journal')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Volume</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="12" value={form.volume} onChange={set('volume')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Issue</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="3" value={form.issue} onChange={set('issue')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Pages</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="123–145" value={form.pages} onChange={set('pages')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>DOI</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="10.1234/example" value={form.doi} onChange={set('doi')} />
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>URL</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="https://…" value={form.url} onChange={set('url')} />
        </div>
      </div>

      {apaPreview && (
        <div className="rounded-xl px-4 py-3" style={{ background: BG, border: `1px solid ${BORDER}` }}>
          <p className="font-mono mb-1" style={{ fontSize: 10, color: FAINT, letterSpacing: '0.06em' }}>APA PREVIEW</p>
          <p className="font-sans text-xs leading-relaxed italic" style={{ color: MUTED }}>{apaPreview}</p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ border: `1px solid ${BORDER}`, color: MUTED }}
          onMouseEnter={e => e.currentTarget.style.background = BG}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          Cancel
        </button>
        <button onClick={() => onSave(form)} disabled={!canSave || saving}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium transition-all disabled:opacity-50"
          style={{ background: GOLD, color: '#FFFFFF' }}
          onMouseEnter={e => { if (!saving && canSave) e.currentTarget.style.background = '#3b82f6' }}
          onMouseLeave={e => e.currentTarget.style.background = GOLD}>
          {saving && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
          Save Reference
        </button>
      </div>
    </div>
  )
}

// ─── Paste & Parse modal ──────────────────────────────────────────────────────

function PasteParseModal({ onImport, onClose }) {
  const [raw,       setRaw]       = useState('')
  const [parsing,   setParsing]   = useState(false)
  const [parsed,    setParsed]    = useState(null)
  const [error,     setError]     = useState('')
  const [importing, setImporting] = useState(false)

  const parse = async () => {
    if (!raw.trim()) return
    setParsing(true); setError(''); setParsed(null)
    try {
      const { data } = await api.post('/references/parse', { rawText: raw, save: false })
      setParsed(data.references || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Parse failed. Try again.')
    } finally { setParsing(false) }
  }

  const importAll = async () => {
    if (!parsed?.length) return
    setImporting(true)
    try {
      const { data } = await api.post('/references/parse', { rawText: raw, save: true })
      onImport(data.references || [])
    } catch { setError('Import failed.') } finally { setImporting(false) }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles style={{ width: 15, height: 15, color: GOLD }} />
            <p className="font-sans font-semibold text-sm" style={{ color: TEXT }}>Paste &amp; Parse with AI</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors"
            onMouseEnter={e => e.currentTarget.style.background = BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <X style={{ width: 15, height: 15, color: FAINT }} />
          </button>
        </div>

        {!parsed ? (
          <>
            <p className="font-sans text-xs mb-3" style={{ color: FAINT }}>
              Paste one or more references in any format — APA, MLA, Chicago, numbered list.
            </p>
            <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={8}
              placeholder={`Smith, J. (2020). The title of the article. Journal of Examples, 12(3), 45–67.\nhttps://doi.org/10.1234/example\n\nJones, M. & Brown, K. (2019). Another study. Academic Press.`}
              className={inputCls + ' resize-none'} style={{ ...inputSty, lineHeight: 1.6 }} {...focusHandlers} />
            {error && (
              <div className="flex items-center gap-2 mt-2 text-xs font-sans" style={{ color: '#DC2626' }}>
                <AlertCircle style={{ width: 12, height: 12 }} />{error}
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
                style={{ border: `1px solid ${BORDER}`, color: MUTED }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Cancel
              </button>
              <button onClick={parse} disabled={!raw.trim() || parsing}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium disabled:opacity-50 transition-all"
                style={{ background: GOLD, color: '#FFFFFF' }}>
                {parsing ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 13, height: 13 }} />}
                Parse with AI
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="font-sans text-xs mb-3" style={{ color: FAINT }}>
              {parsed.length} reference{parsed.length !== 1 ? 's' : ''} found. Review before importing.
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-4 scrollbar-thin">
              {parsed.map((r, i) => (
                <div key={i} className="rounded-xl px-3.5 py-3" style={{ background: BG, border: `1px solid ${BORDER}` }}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-sans text-xs font-semibold" style={{ color: TEXT }}>{r.title}</p>
                    <TypeChip type={r.type} />
                  </div>
                  <p className="font-sans text-xs mt-1" style={{ color: MUTED }}>{r.authors} {r.year ? `(${r.year})` : ''}</p>
                  {r.apa_formatted && (
                    <p className="font-mono text-xs mt-1.5 italic leading-relaxed" style={{ color: FAINT, fontSize: 10 }}>{r.apa_formatted}</p>
                  )}
                </div>
              ))}
            </div>
            {error && <p className="font-sans text-xs mb-3" style={{ color: '#DC2626' }}>{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setParsed(null)}
                className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
                style={{ border: `1px solid ${BORDER}`, color: MUTED }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Back
              </button>
              <button onClick={importAll} disabled={importing}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium disabled:opacity-50 transition-all"
                style={{ background: '#16A34A', color: '#FFFFFF' }}>
                {importing && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Import All ({parsed.length})
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ─── Add / Edit modal ─────────────────────────────────────────────────────────

function AddModal({ existing, onSave, onClose }) {
  const [tab,    setTab]    = useState('manual')
  const [saving, setSaving] = useState(false)

  const save = async form => {
    setSaving(true)
    try {
      const apa = buildApa(form)
      if (existing) {
        const { data } = await api.put(`/references/${existing.id}`, { ...form, apaFormatted: apa })
        onSave(data, 'edit')
      } else {
        const { data } = await api.post('/references', { ...form, apaFormatted: apa })
        onSave(data, 'add')
      }
    } catch { setSaving(false) }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-sans font-semibold text-sm" style={{ color: TEXT }}>
            {existing ? 'Edit Reference' : 'Add Reference'}
          </p>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors"
            onMouseEnter={e => e.currentTarget.style.background = BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <X style={{ width: 15, height: 15, color: FAINT }} />
          </button>
        </div>

        {!existing && (
          <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: BG, border: `1px solid ${BORDER}` }}>
            {[{ id: 'manual', label: 'Manual Entry' }, { id: 'paste', label: 'Paste & Parse' }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="flex-1 py-2 rounded-lg font-sans text-xs font-medium transition-all"
                style={{
                  background: tab === t.id ? SURFACE : 'transparent',
                  color: tab === t.id ? TEXT : FAINT,
                  border: tab === t.id ? `1px solid ${BORDER}` : '1px solid transparent',
                  boxShadow: tab === t.id ? '0 1px 3px rgba(12,22,41,0.06)' : 'none',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === 'manual' || existing
          ? <ManualForm
              initial={existing ? {
                ...existing,
                year:    existing.year    || '',
                doi:     existing.doi     || '',
                url:     existing.url     || '',
                journal: existing.journal || '',
                volume:  existing.volume  || '',
                issue:   existing.issue   || '',
                pages:   existing.pages   || '',
              } : EMPTY_REF}
              onSave={save}
              onCancel={onClose}
              saving={saving}
            />
          : <PasteParseModal onImport={refs => onSave(refs, 'bulk')} onClose={onClose} />
        }
      </div>
    </Modal>
  )
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ field, sortKey, dir }) {
  if (sortKey !== field) return <ChevronUp style={{ width: 11, height: 11, color: '#CBD8E8' }} />
  return dir === 'asc'
    ? <ChevronUp   style={{ width: 11, height: 11, color: GOLD }} />
    : <ChevronDown style={{ width: 11, height: 11, color: GOLD }} />
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReferencesPage() {
  const [refs,      setRefs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [view,      setView]      = useState('table')
  const [modal,     setModal]     = useState(null)
  const [sortKey,   setSortKey]   = useState('year')
  const [sortDir,   setSortDir]   = useState('desc')
  const [deleting,  setDeleting]  = useState(null)
  const [showPaste, setShowPaste] = useState(false)

  useEffect(() => { load() }, [])

  const load = async () => {
    try {
      const { data } = await api.get('/references')
      setRefs(data || [])
    } catch (err) {
      console.error('Load references error:', err)
    } finally { setLoading(false) }
  }

  const sorted = [...refs].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey]
    if (sortKey === 'year') { va = va || 0; vb = vb || 0 }
    else { va = (va || '').toLowerCase(); vb = (vb || '').toLowerCase() }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
  })

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const handleSave = (data, mode) => {
    if (mode === 'bulk') setRefs(prev => [...(Array.isArray(data) ? data : [data]), ...prev])
    else if (mode === 'edit') setRefs(prev => prev.map(r => r.id === data.id ? data : r))
    else setRefs(prev => [data, ...prev])
    setModal(null); setShowPaste(false)
  }

  const handleDelete = async id => {
    setDeleting(id)
    try { await api.delete(`/references/${id}`); setRefs(prev => prev.filter(r => r.id !== id)) }
    catch (err) { console.error('Delete error:', err) }
    finally { setDeleting(null) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 3.5rem)' }}>
        <Loader2 style={{ width: 22, height: 22, color: GOLD, animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  return (
    <div className="max-w-6xl">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-display text-xl font-semibold" style={{ color: TEXT }}>Reference Library</h1>
            <p className="font-sans text-xs mt-0.5" style={{ color: FAINT }}>
              Manage, import and AI-parse your academic references
            </p>
          </div>
          <span className="font-mono text-xs px-2.5 py-1 rounded-lg"
            style={{ background: GOLD_BG, color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.2)' }}>
            {refs.length} refs
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex gap-0.5 p-1 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }}>
            <button onClick={() => setView('table')} className="p-1.5 rounded-md transition-all"
              style={{ background: view === 'table' ? SURFACE : 'transparent', color: view === 'table' ? TEXT : FAINT,
                       boxShadow: view === 'table' ? '0 1px 2px rgba(12,22,41,0.06)' : 'none' }}>
              <LayoutList style={{ width: 14, height: 14 }} />
            </button>
            <button onClick={() => setView('card')} className="p-1.5 rounded-md transition-all"
              style={{ background: view === 'card' ? SURFACE : 'transparent', color: view === 'card' ? TEXT : FAINT,
                       boxShadow: view === 'card' ? '0 1px 2px rgba(12,22,41,0.06)' : 'none' }}>
              <LayoutGrid style={{ width: 14, height: 14 }} />
            </button>
          </div>
          <button onClick={() => setShowPaste(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl font-sans text-xs font-medium transition-all"
            style={{ border: `1px solid ${BORDER}`, color: MUTED, background: SURFACE }}
            onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = TEXT }}
            onMouseLeave={e => { e.currentTarget.style.background = SURFACE; e.currentTarget.style.color = MUTED }}>
            <Sparkles style={{ width: 13, height: 13 }} />Paste &amp; Parse
          </button>
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-sans text-xs font-semibold transition-all"
            style={{ background: GOLD, color: '#FFFFFF' }}
            onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.background = GOLD}>
            <Plus style={{ width: 13, height: 13 }} />Add Reference
          </button>
        </div>
      </div>

      {/* Empty state */}
      {refs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: BG, border: `1px solid ${BORDER}` }}>
            <BookOpen style={{ width: 24, height: 24, color: FAINT }} />
          </div>
          <div className="text-center">
            <p className="font-sans text-sm font-semibold mb-1" style={{ color: TEXT }}>Your reference library is empty</p>
            <p className="font-sans text-xs" style={{ color: FAINT }}>Add references manually or paste and parse them with AI.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPaste(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
              style={{ border: `1px solid ${BORDER}`, color: MUTED, background: SURFACE }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = SURFACE}>
              <Sparkles style={{ width: 14, height: 14 }} />Paste &amp; Parse
            </button>
            <button onClick={() => setModal('add')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
              style={{ background: GOLD, color: '#FFFFFF' }}
              onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
              onMouseLeave={e => e.currentTarget.style.background = GOLD}>
              <Plus style={{ width: 14, height: 14 }} />Add Reference
            </button>
          </div>
        </div>
      )}

      {/* Table view */}
      {view === 'table' && refs.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-card" style={{ border: `1px solid ${BORDER}`, background: SURFACE }}>
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${BORDER}`, background: BG }}>
                {[['#','',40],['Authors','authors',180],['Year','year',70],['Title','title',null],['Type','type',90]].map(([label, key, w]) => (
                  <th key={label}
                    onClick={() => key && toggleSort(key)}
                    className={`text-left px-4 py-3 font-sans ${key ? 'cursor-pointer' : ''}`}
                    style={{ width: w || undefined, userSelect: 'none', fontSize: 11, fontWeight: 500, color: FAINT, letterSpacing: '0.04em' }}>
                    <span className="flex items-center gap-1">
                      {label.toUpperCase()}{key && <SortIcon field={key} sortKey={sortKey} dir={sortDir} />}
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3" style={{ width: 80, fontSize: 11, fontWeight: 500, color: FAINT, textAlign: 'left' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((ref, i) => (
                <tr key={ref.id} style={{ borderBottom: `1px solid ${BORDER}`, background: i % 2 === 0 ? SURFACE : '#FAFBFE' }}>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: FAINT }}>{i + 1}</td>
                  <td className="px-4 py-3 font-sans text-xs" style={{ color: MUTED, maxWidth: 180 }}>
                    <span className="truncate block" style={{ maxWidth: 175 }}>{ref.authors}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-medium" style={{ color: MUTED }}>{ref.year || '—'}</td>
                  <td className="px-4 py-3">
                    <p className="font-sans text-xs font-medium" style={{ color: TEXT, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.title}</p>
                    {ref.apa_formatted && (
                      <p className="font-mono text-xs mt-0.5 italic" style={{ color: FAINT, fontSize: 10, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.apa_formatted}</p>
                    )}
                  </td>
                  <td className="px-4 py-3"><TypeChip type={ref.type} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => setModal(ref)}
                        className="p-1.5 rounded-lg transition-all" title="Edit"
                        style={{ color: FAINT }}
                        onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = MUTED }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                        <Pencil style={{ width: 12, height: 12 }} />
                      </button>
                      <button onClick={() => handleDelete(ref.id)} disabled={deleting === ref.id}
                        className="p-1.5 rounded-lg transition-all disabled:opacity-40" title="Delete"
                        style={{ color: FAINT }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                        {deleting === ref.id
                          ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
                          : <Trash2 style={{ width: 12, height: 12 }} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Card view */}
      {view === 'card' && refs.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {sorted.map(ref => (
            <div key={ref.id} className="rounded-2xl p-5 space-y-3 group relative transition-all shadow-card"
              style={{ background: SURFACE, border: `1px solid ${BORDER}` }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(12,22,41,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(12,22,41,0.08)'}>
              <div className="flex items-start justify-between gap-2">
                <TypeChip type={ref.type} />
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setModal(ref)} className="p-1 rounded-lg transition-all"
                    style={{ color: FAINT }}
                    onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = MUTED }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                    <Pencil style={{ width: 12, height: 12 }} />
                  </button>
                  <button onClick={() => handleDelete(ref.id)} className="p-1 rounded-lg transition-all"
                    style={{ color: FAINT }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              </div>
              <p className="font-sans text-sm font-semibold leading-snug line-clamp-2" style={{ color: TEXT }}>{ref.title}</p>
              <p className="font-sans text-xs" style={{ color: MUTED }}>{ref.authors}</p>
              {ref.year && <p className="font-mono text-xs" style={{ color: FAINT }}>{ref.year}</p>}
              {ref.apa_formatted && (
                <p className="font-mono text-xs italic leading-relaxed line-clamp-3" style={{ color: FAINT, fontSize: 10 }}>
                  {ref.apa_formatted}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {(modal === 'add' || (modal && modal !== 'add')) && (
        <AddModal existing={modal === 'add' ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}

      {showPaste && (
        <PasteParseModal onImport={refs => { handleSave(refs, 'bulk'); setShowPaste(false) }} onClose={() => setShowPaste(false)} />
      )}
    </div>
  )
}
